import { ROLES } from '../../../../../Permission'
import {
  FULL_USER_STATUSES_OPTIONS,
  ROLES_NAMES
} from '../../../../../../constants'
import { ColumnsType } from 'antd/es/table'
import { Sorter } from '../../../../../../components/Sorter/Sorter'
import { SortOrder } from 'antd/es/table/interface'
import { CellWithSubtitle } from '../../../../../../components/CellWithSubtitle/CellWithSubtitle'
import { Link } from '../../../../../../components/Link'
import { routes } from '../../../../../../router'
import { Dropdown } from '../../../../../../components/Dropdown'
import { ReactComponent as FilterApplied16 } from '../../../../../../assets/svg/FilterApplied16.svg'
import { ReactComponent as Filter16 } from '../../../../../../assets/svg/Filter16.svg'
import {
  Status,
  USER_STATUS_TYPES_TO_CLASSNAME
} from '../../../../../../components/Status'
import { fullFormatPhoneNumber } from '../../../../../../helper/common'
import { DescriptionCell } from '../../../../../../components/DescriptionCell/DescriptionCell'
import {
  HealthSystemUser,
  HealthSystemUsersFilters,
  HealthSystemUsersFiltersFunc
} from '../../../../HealthSystems/HealthSystemUsers/types'
import { getDepartmentsList, getHospitalsList } from '../../../../api'
import { getContractCategoriesListByRole } from '../../../../HealthSystems/HealthSystemUsers/api'
import { MY_COMMUNITY_KEYS } from '../../../../constants'
import { StartChat } from '../../../../../../components/StartChat/StartChat'
import { UserMenu } from '../../HospitalsTable/HospitalDetails/UsersTable/UserMenu'
import { HSUsersTableColumnsFunc } from './types'
import { fetchContractCategories } from 'pages/CRUDUser/api'

export const ROLES_LIST = [
  {
    label: ROLES_NAMES[ROLES.VICE_PRESIDENT],
    value: 'community_sourcing_leader'
  },
  {
    label: ROLES_NAMES[ROLES.CLINICAL_COORDINATOR],
    value: ROLES.CLINICAL_COORDINATOR
  },
  { label: ROLES_NAMES[ROLES.CONTRACT_STEWARD], value: ROLES.CONTRACT_STEWARD },
  { label: ROLES_NAMES[ROLES.COMMUNITY_RCA], value: ROLES.COMMUNITY_RCA },
  { label: ROLES_NAMES[ROLES.LEGAL], value: ROLES.LEGAL },
  { label: ROLES_NAMES[ROLES.ANALYST], value: ROLES.ANALYST },
  { label: ROLES_NAMES[ROLES.HOSPITAL_ADMIN], value: ROLES.HOSPITAL_ADMIN },
  { label: ROLES_NAMES[ROLES.FINANCE], value: ROLES.FINANCE },
  { label: ROLES_NAMES[ROLES.HOSPITAL_USER], value: ROLES.HOSPITAL_USER }
]

export const columns = ({
  applyFilters,
  filters,
  selectedRole,
  hsId,
  onStartChat,
  onArchive,
  canEditUser,
  canCrudUsersInHSDetails
}: HSUsersTableColumnsFunc): ColumnsType<HealthSystemUser> => {
  const cols: ColumnsType<HealthSystemUser> = [
    {
      dataIndex: 'name',
      key: 'name',
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<HealthSystemUser>
          dataIndex="name"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Name
        </Sorter>
      ),
      render: (_, { first_name, last_name, uuid }) => (
        <CellWithSubtitle
          title={
            <Link
              to={`${routes.createUserProfile}/${uuid}?fromCommunity=true`}
              state={{
                redirectTab: MY_COMMUNITY_KEYS.communities,
                selectedHs: hsId
              }}
            >
              {first_name} {last_name}
            </Link>
          }
        />
      ),
      sorter: true,
      showSorterTooltip: false
    },
    {
      dataIndex: 'status',
      key: 'status',
      width: 126,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<HealthSystemUser>
          dataIndex="status"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Status
        </Sorter>
      ),
      filterDropdown: (props) => (
        <Dropdown.Filter<
          HealthSystemUsersFilters,
          HealthSystemUsersFiltersFunc,
          unknown
        >
          items={FULL_USER_STATUSES_OPTIONS}
          applyFilters={applyFilters}
          appliedFilters={filters}
          field="statuses"
          {...props}
        />
      ),
      render: (_, { status, uuid }) => (
        <div className="row gap-8 align-center">
          <Status.Default className={USER_STATUS_TYPES_TO_CLASSNAME[status]}>
            {status}
          </Status.Default>
          {status === 'Active' && (
            <StartChat onClick={() => onStartChat(uuid)} />
          )}
        </div>
      ),
      filterIcon: !!filters?.statuses?.length ? (
        <FilterApplied16 />
      ) : (
        <Filter16 />
      ),
      filterSearch: true,
      sorter: true,
      showSorterTooltip: false
    },
    {
      dataIndex: 'email',
      key: 'email',
      width: 220,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<HealthSystemUser>
          dataIndex="email"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Email
        </Sorter>
      ),
      sorter: true,
      showSorterTooltip: false
    },
    {
      dataIndex: 'phone_number',
      key: 'phone_number',
      title: () => <div className="min-width-200">Phone number</div>,
      width: 220,
      render: (_, { phone_number }) => fullFormatPhoneNumber(phone_number),
      sorter: false,
      showSorterTooltip: false
    }
  ]
  if (!selectedRole) {
    cols.splice(0, 0, {
      dataIndex: 'role',
      key: 'role',
      width: 180,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<HealthSystemUser>
          dataIndex="role"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Role
        </Sorter>
      ),
      render: (text) => ROLES_NAMES[text] ?? <>–</>,
      sorter: true,
      showSorterTooltip: false
    })
  }
  if (
    !selectedRole ||
    (selectedRole !== ROLES.COMMUNITY_RCA &&
      selectedRole !== ROLES.HOSPITAL_ADMIN &&
      selectedRole !== ROLES.FINANCE)
  ) {
    cols.push({
      dataIndex: 'contract_categories',
      key: 'contract_categories',
      width: 240,
      title: 'Contract categories',
      filterDropdown: (props) => (
        <Dropdown.Filter<
          HealthSystemUsersFilters,
          HealthSystemUsersFiltersFunc,
          unknown
        >
          applyFilters={applyFilters}
          appliedFilters={filters}
          searchCallback={() =>
            selectedRole
              ? getContractCategoriesListByRole(hsId, selectedRole)
              : fetchContractCategories()
          }
          field="contract_categories"
          asyncSearch
          {...props}
        />
      ),
      render: (_, { contract_categories }) => {
        return (
          <DescriptionCell
            content={contract_categories}
            text={contract_categories}
          />
        )
      },
      filterIcon: !!filters?.contract_categories?.length ? (
        <FilterApplied16 />
      ) : (
        <Filter16 />
      ),
      filterSearch: true,
      showSorterTooltip: false
    })
  }
  if (
    !selectedRole ||
    selectedRole === ROLES.HOSPITAL_ADMIN ||
    selectedRole === ROLES.HOSPITAL_USER
  ) {
    cols.push({
      dataIndex: 'hospitals',
      key: 'hospitals',
      title: 'Hospital',
      width: 240,
      filterDropdown: (props) => (
        <Dropdown.Filter<
          HealthSystemUsersFilters,
          HealthSystemUsersFiltersFunc,
          unknown
        >
          applyFilters={applyFilters}
          appliedFilters={filters}
          searchCallback={() => getHospitalsList(hsId)}
          field="hospitals"
          asyncSearch
          {...props}
        />
      ),
      filterIcon: !!filters?.hospitals?.length ? (
        <FilterApplied16 />
      ) : (
        <Filter16 />
      ),
      filterSearch: true,
      sorter: false,
      showSorterTooltip: false,
      render: (_, { hospitals }) => hospitals ?? '–'
    })
    if (selectedRole !== ROLES.HOSPITAL_ADMIN || !selectedRole) {
      cols.push({
        dataIndex: 'departments',
        key: 'departments',
        width: 240,
        title: ({ sortOrder, sortColumn }) => (
          <Sorter<HealthSystemUser>
            dataIndex="departments"
            sortColumn={sortColumn}
            sortOrder={sortOrder as SortOrder}
          >
            Departments
          </Sorter>
        ),
        filterDropdown: (props) => (
          <Dropdown.Filter<
            HealthSystemUsersFilters,
            HealthSystemUsersFiltersFunc,
            unknown
          >
            applyFilters={applyFilters}
            appliedFilters={filters}
            searchCallback={() => getDepartmentsList(hsId)}
            field="departments"
            asyncSearch
            {...props}
          />
        ),
        render: (_, { departments }) => {
          return <DescriptionCell content={departments} text={departments} />
        },
        filterIcon: !!filters?.departments?.length ? (
          <FilterApplied16 />
        ) : (
          <Filter16 />
        ),
        filterSearch: true,
        sorter: true,
        showSorterTooltip: false
      })
    }
  }
  if (canEditUser && canCrudUsersInHSDetails)
    cols.push({
      title: '',
      dataIndex: 'action',
      key: 'action',
      width: 40,
      fixed: 'right',
      render: (_, row) => {
        const _user = {
          ...row,
          contract_categories: '',
          departments: []
        }
        const canSeeActions = !('can_be_managed' in row) || row.can_be_managed
        return canSeeActions ? (
          <UserMenu user={_user} onArchive={onArchive} />
        ) : null
      }
    })
  return cols
}
